import React from "react";
import { linkedVideo, sanitizeInlineHtml, type PageHtmlFile } from "@eaco/forms-core";

/**
 * A page body, read-only, as plain React.
 *
 * DocumentView renders every text block through a tiptap editor (with the
 * collab extensions) even when nothing can be edited, which is right for the
 * canvas and heavy for a feed card or a published page. This renders the same
 * blocks with nothing but React and @eaco/forms-core, and looks like the
 * editor's own read view: same type scale, list markers, spacing and indent.
 *
 * Imported from its own entry (`@eaco/forms-ui-web/readonly`) so pulling it in
 * never drags the editor graph along. Text blocks store the editor's HTML,
 * which goes through forms-core's sanitizeInlineHtml allow-list before it is
 * inserted. Workspace-only blocks (entity cards, job lists, page links)
 * render nothing unless the host supplies them through `renderBlock`; an
 * action item shows by its name and done state, the part a reader outside the
 * workspace may see.
 *
 * `print` is for paper and PDFs: a video becomes its still frame with a play
 * mark (a player prints blank) and toggles print open.
 */

export type ReadOnlyBlock = {
  id?: string;
  typeId?: string;
  data?: Record<string, unknown>;
  elements?: unknown[];
  archived?: boolean;
};

export interface PageReadOnlyProps {
  /** The page body: `{ elements: [...] }`. */
  content: unknown;
  /** An attachment by id, signed; a media block whose file comes back empty renders nothing. */
  resolveFile?: (attachmentId: number) => PageHtmlFile | null | undefined;
  /**
   * The host's renderer for a block, when it has one: return a node to use it,
   * undefined to keep the default. How a host adds the workspace blocks this
   * view leaves out (an entity card, a job list, a page link).
   */
  renderBlock?: (block: ReadOnlyBlock) => React.ReactNode | undefined;
  /** Render for paper: video stills instead of players, toggles open. */
  print?: boolean;
  className?: string;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");

const attachmentIdOf = (data: Record<string, unknown>): number | null => {
  const id = Number(data.attachmentId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const indentOf = (block: ReadOnlyBlock): number => {
  const n = Number(block.data?.indent);
  return Number.isFinite(n) && n > 0 ? Math.min(8, Math.floor(n)) : 0;
};

/** Blocks that only mean something inside a workspace. */
const WORKSPACE_ONLY = /^(page-entity-|page-job-|page-link$|page-break$)/;

const LIST_TYPES = new Set(["page-bullet-list", "page-numbered-list", "page-todo-list", "page-action-item"]);

/** Blocks shown as a checkable row: to-dos, and action items by name. */
const CHECKABLE = new Set(["page-todo-list", "page-action-item"]);

// The editor's read-view type (PageRichText, PageHeading).
const TEXT =
  "font-jakarta text-[16px] leading-[1.68] text-[#41403c] break-words " +
  "[&_a]:text-[#2f6fdb] [&_a]:underline [&_a]:underline-offset-2 [&_strong]:font-semibold [&_strong]:text-[#2a2723] " +
  "[&_code]:rounded-[5px] [&_code]:bg-[#f3f1ee] [&_code]:px-[5px] [&_code]:py-[1px] [&_code]:font-jbmono [&_code]:text-[0.9em] " +
  "[&_mark]:bg-[#fff1b8] [&_mark]:px-[1px] " +
  // Stated outright: host apps reset these (the web app's base CSS sets em
  // upright), and the editor's own view styles them itself.
  "[&_em]:italic [&_u]:underline [&_s]:line-through";

const HEADING: Record<number, string> = {
  1: "text-[32px] font-extrabold leading-[1.18] tracking-[-0.021em] text-[#1f1d1a]",
  2: "text-[24px] font-bold leading-[1.22] tracking-[-0.015em] text-[#22201c]",
  3: "text-[19px] font-bold leading-[1.28] tracking-[-0.01em] text-[#2a2723]",
  4: "text-[16px] font-bold leading-[1.3] text-[#33302b]",
};

/** Sanitised editor HTML as a node; null when there is no text. */
function Html({ html, className, as: Tag = "div" }: { html: string; className?: string; as?: "div" | "span" }) {
  const clean = sanitizeInlineHtml(html).trim();
  if (!clean) return null;
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
}

/**
 * A numbered item's number, the word-processor way: count back through the
 * run of numbered items, at this indent only, stopping at a shallower one.
 */
function itemNumber(siblings: ReadOnlyBlock[], index: number): number {
  const level = indentOf(siblings[index]);
  let n = 1;
  for (let i = index - 1; i >= 0; i--) {
    const prev = siblings[i];
    if (prev.typeId !== "page-numbered-list") break;
    const depth = indentOf(prev);
    if (depth > level) continue;
    if (depth < level) break;
    n++;
  }
  return n;
}

function ListItem({ block, marker }: { block: ReadOnlyBlock; marker: React.ReactNode }) {
  const data = block.data ?? {};
  const checked = CHECKABLE.has(block.typeId ?? "") && data.checked === true;
  return (
    <div className="flex gap-[11px]">
      {marker}
      <div className={`min-w-0 flex-1 ${checked ? "line-through opacity-50" : ""}`}>
        <Html html={str(data.content)} className={TEXT} />
        {!str(data.content).trim() && <div className={TEXT}>&nbsp;</div>}
      </div>
    </div>
  );
}

const Bullet = () => (
  <span aria-hidden className="flex h-[27px] w-[9px] flex-none select-none items-center justify-center">
    <span className="h-[5.5px] w-[5.5px] rounded-full bg-[#a5a199]" />
  </span>
);

const Check = ({ checked }: { checked: boolean }) => (
  <span
    role="img"
    aria-label={checked ? "Done" : "Not done"}
    className={`mt-[5px] flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-[5px] border-[1.6px] ${
      checked ? "border-[#2f6fdb] bg-[#2f6fdb] text-white" : "border-[#cbc7bf] bg-white"
    }`}
  >
    {checked && (
      <svg viewBox="0 0 12 12" className="h-[9px] w-[9px]" aria-hidden>
        <path d="M2.5 6.2 5 8.6l4.6-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

function Table({ data }: { data: Record<string, unknown> }) {
  const legacy = Array.isArray(data.columns) ? (data.columns as unknown[]).map(str) : null;
  const rowsIn = Array.isArray(data.rows) ? (data.rows as unknown[]) : [];
  // Legacy tables kept their header names apart; they read as row 0.
  const rows = [...(legacy ? [legacy] : []), ...rowsIn.map((r) => (Array.isArray(r) ? r.map(str) : []))];
  if (!rows.length) return null;
  const headerRow = typeof data.headerRow === "boolean" ? data.headerRow : !!legacy;
  const headerColumn = data.headerColumn === true;
  return (
    <div className="max-w-full overflow-x-auto rounded-[9px] border border-[#e7e5e1] font-jakarta">
      <table className="w-full border-separate border-spacing-0 text-[13.5px] text-[#41403c]">
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => {
                const header = (headerRow && r === 0) || (headerColumn && c === 0);
                const edge = `${r < rows.length - 1 ? "border-b" : ""} ${c < row.length - 1 ? "border-r" : ""} border-[#ecebe7]`;
                return header ? (
                  <th key={c} scope={r === 0 && headerRow ? "col" : "row"} className={`bg-[#f7f6f3] px-[12px] py-[8px] text-left font-semibold text-[#2a2723] ${edge}`}>
                    {cell}
                  </th>
                ) : (
                  <td key={c} className={`px-[12px] py-[8px] align-top ${edge}`}>
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const formatSize = (bytes: number | null | undefined): string => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function Block({
  block,
  siblings,
  index,
  props,
}: {
  block: ReadOnlyBlock;
  siblings: ReadOnlyBlock[];
  index: number;
  props: PageReadOnlyProps;
}): React.ReactElement | null {
  const typeId = block.typeId ?? "";
  const data = block.data ?? {};
  const children = Array.isArray(block.elements) ? block.elements : [];
  const file = () => {
    const id = attachmentIdOf(data);
    return id && props.resolveFile ? props.resolveFile(id) ?? null : null;
  };

  const custom = props.renderBlock?.(block);
  if (custom !== undefined) return <>{custom}</>;
  if (WORKSPACE_ONLY.test(typeId)) return null;

  const heading = /^page-heading-([1-4])$/.exec(typeId);
  if (heading) {
    const level = Number(heading[1]);
    const Tag = `h${Math.min(6, level + 1)}` as "h2";
    const text = str(data.text).trim();
    if (text) return <Tag className={`font-jakarta ${HEADING[level]}`}>{text}</Tag>;
    const html = sanitizeInlineHtml(str(data.content)).trim();
    return html ? <Tag className={`font-jakarta ${HEADING[level]}`} dangerouslySetInnerHTML={{ __html: html }} /> : null;
  }

  switch (typeId) {
    case "page":
    case "pages":
      return <Blocks elements={children} props={props} />;

    case "page-text":
      return <Html html={str(data.content)} className={TEXT} />;

    case "page-bullet-list":
      return <ListItem block={block} marker={<Bullet />} />;

    case "page-numbered-list":
      return (
        <ListItem
          block={block}
          marker={
            <span className="min-w-[1.4em] flex-none select-none text-right font-jakarta text-[16px] font-medium leading-[1.68] tabular-nums text-[#8f8b83]">
              {itemNumber(siblings, index)}.
            </span>
          }
        />
      );

    case "page-todo-list":
    case "page-action-item":
      return <ListItem block={block} marker={<Check checked={data.checked === true} />} />;

    case "page-quote": {
      const html = sanitizeInlineHtml(str(data.content)).trim();
      return html ? (
        <blockquote className={`border-l-[3px] border-[#37342f] py-[2px] pl-[16px] ${TEXT}`} dangerouslySetInnerHTML={{ __html: html }} />
      ) : null;
    }

    case "page-code": {
      const code = str(data.content);
      return code.trim() ? (
        <pre className="overflow-x-auto rounded-[11px] bg-[#26241f] px-[16px] py-[15px] font-jbmono text-[13px] leading-[1.6] text-[#e6e3dc]">
          <code>{code}</code>
        </pre>
      ) : null;
    }

    case "page-divider":
      return (
        <div className="py-[6px]">
          <hr className="border-0 border-t border-[#ebeae7]" />
        </div>
      );

    case "page-callout":
      return children.length ? (
        <div className="flex items-start gap-[12px] rounded-[10px] border border-[#ecebe6] bg-[#f7f6f3] px-[16px] py-[14px]">
          <span aria-hidden className="flex-none select-none text-[18px] leading-[1.5]">
            💡
          </span>
          <div className="min-w-0 flex-1">
            <Blocks elements={children} props={props} />
          </div>
        </div>
      ) : null;

    case "page-card":
      return children.length ? (
        <div className="rounded-[10px] border border-[#e7e5e1] bg-white px-[17px] py-[15px] shadow-[0_1px_2px_rgba(20,18,15,0.04)]">
          <Blocks elements={children} props={props} />
        </div>
      ) : null;

    case "page-toggle": {
      const title = str(data.content) || str(data.title);
      if (props.print) {
        return (
          <div>
            <Html html={title || "Details"} className="font-jakarta text-[16px] font-semibold leading-[1.5] text-[#2a2723]" />
            {children.length > 0 && (
              <div className="ml-[10px] border-l border-[#eceae5] pl-[18px] pt-[2px]">
                <Blocks elements={children} props={props} />
              </div>
            )}
          </div>
        );
      }
      return (
        <details open={data.collapsed !== true} className="group">
          <summary className="flex cursor-pointer list-none items-start gap-[8px] px-[4px] py-[3px] [&::-webkit-details-marker]:hidden">
            <svg viewBox="0 0 10 10" aria-hidden className="mt-[8px] h-[10px] w-[10px] flex-none text-[#8f8b83] transition-transform group-open:rotate-90">
              <path d="M3 1.5 7 5 3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <Html html={title || "Details"} className="min-w-0 flex-1 font-jakarta text-[16px] font-semibold leading-[1.5] text-[#2a2723]" />
          </summary>
          {children.length > 0 && (
            <div className="ml-[10px] border-l border-[#eceae5] pl-[18px] pt-[2px]">
              <Blocks elements={children} props={props} />
            </div>
          )}
        </details>
      );
    }

    case "page-table":
      return <Table data={data} />;

    case "page-image": {
      const linked = str(data.imageUrl);
      const url = (/^https?:\/\//i.test(linked) ? linked : null) ?? file()?.url ?? null;
      if (!url) return null;
      const fit = str(data.fit) === "cover" ? "object-cover" : "object-contain";
      return <img src={url} alt={str(data.alt)} loading="lazy" decoding="async" className={`max-h-[520px] w-full rounded-md ${fit}`} />;
    }

    case "page-video": {
      const caption = str(data.caption).trim();
      const link = str(data.videoUrl).trim();
      let player: React.ReactNode = null;
      if (props.print) {
        // A player prints blank: show the still frame with a play mark, the
        // faithful static form of the video.
        const linked = link ? linkedVideo(link) : null;
        const poster = linked?.kind === "embed" ? linked.poster : !link ? file()?.thumbnailUrl : null;
        if (link ? !!linked : !!file()?.url) {
          player = (
            <div className="relative aspect-video overflow-hidden rounded-[11px] bg-[#14120f]">
              {poster && <img src={poster} alt={caption} className="absolute inset-0 h-full w-full object-cover" />}
              <span className="absolute left-1/2 top-1/2 flex h-[54px] w-[54px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55">
                <svg viewBox="0 0 12 14" aria-hidden className="ml-[3px] h-[18px] w-[16px] fill-white">
                  <path d="M0 0v14l12-7z" />
                </svg>
              </span>
            </div>
          );
        }
      } else if (link) {
        const video = linkedVideo(link);
        if (video?.kind === "embed") {
          player = (
            <iframe
              src={video.src}
              title={caption || "Video"}
              loading="lazy"
              allow={video.allow}
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="block aspect-video w-full rounded-[11px] border-0 bg-[#14120f]"
            />
          );
        } else if (video) {
          player = <video src={video.src} controls preload="metadata" playsInline className="block w-full rounded-[11px] bg-[#14120f]" />;
        }
      } else {
        const f = file();
        if (f?.url) {
          player = (
            <video
              src={f.url}
              poster={f.thumbnailUrl ?? undefined}
              controls
              preload="metadata"
              playsInline
              className="block w-full rounded-[11px] bg-[#14120f]"
            />
          );
        }
      }
      if (!player) return null;
      return (
        <figure className="m-0">
          {player}
          {caption && <figcaption className="mt-[8px] font-jakarta text-[12.5px] font-medium text-[#8f8b83]">{caption}</figcaption>}
        </figure>
      );
    }

    case "page-audio": {
      const f = file();
      return f?.url ? <audio src={f.url} controls preload="none" className="w-full" /> : null;
    }

    case "page-file": {
      const f = file();
      if (!f?.url) return null;
      const size = formatSize(f.size);
      return (
        <a
          href={f.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-[11px] rounded-[10px] border border-[#e7e5e1] bg-white px-[14px] py-[11px] no-underline hover:bg-[#faf9f7]"
        >
          <span aria-hidden className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[9px] bg-[#f3f1ee] text-[#8f8b83]">
            <svg viewBox="0 0 16 16" className="h-[15px] w-[15px]">
              <path d="M4 1.5h5.2L13 5.3v8.2c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1v-11c0-.6.4-1 1-1Z M9 1.5V5.5h4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="min-w-0 flex-1 truncate font-jakarta text-[13.5px] font-semibold text-[#2a2723]">{f.name?.trim() || "Download"}</span>
          {size && <span className="flex-none font-jakarta text-[12.5px] text-[#8f8b83]">{size}</span>}
        </a>
      );
    }

    case "page-location": {
      const address = str(data.address).trim();
      if (!address) return null;
      return (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${TEXT} block`}
        >
          {address}
        </a>
      );
    }

    default: {
      // A type this view doesn't know yet keeps its prose and its children,
      // so a new block degrades to text rather than a hole.
      const own = str(data.content) ? <Html html={str(data.content)} className={TEXT} /> : null;
      const text = !own && str(data.text).trim() ? <p className={TEXT}>{str(data.text).trim()}</p> : null;
      if (!own && !text && !children.length) return null;
      return (
        <>
          {own ?? text}
          {children.length > 0 && <Blocks elements={children} props={props} />}
        </>
      );
    }
  }
}

/** A run of blocks with the editor's rhythm: 16px apart, list runs 5px. */
function Blocks({ elements, props }: { elements: unknown[]; props: PageReadOnlyProps }) {
  const blocks = elements.filter(
    (raw): raw is ReadOnlyBlock => !!raw && typeof raw === "object" && !(raw as ReadOnlyBlock).archived,
  );
  let previousType: string | null = null;
  return (
    <>
      {blocks.map((block, index) => {
        // A block that will render nothing (a workspace-only block with no
        // host renderer, empty text, a missing file) gets no wrapper and no
        // gap, so the spacing is decided against the last block drawn.
        if (isEmptyBlock(block, props)) return null;
        const typeId = block.typeId ?? "";
        const marginTop =
          previousType === null ? 0 : LIST_TYPES.has(typeId) && previousType === typeId ? 5 : 16;
        previousType = typeId;
        const indent = indentOf(block);
        return (
          <div
            key={block.id ?? index}
            data-pbt={typeId}
            style={{ marginTop: marginTop || undefined, paddingLeft: indent ? indent * 24 : undefined }}
          >
            <Block block={block} siblings={blocks} index={index} props={props} />
          </div>
        );
      })}
    </>
  );
}

/** Whether a block will render nothing, so it gets no wrapper and no gap. */
function isEmptyBlock(block: ReadOnlyBlock, props: PageReadOnlyProps): boolean {
  if (props.renderBlock?.(block) !== undefined) return false;
  const typeId = block.typeId ?? "";
  const data = block.data ?? {};
  if (WORKSPACE_ONLY.test(typeId)) return true;
  if (typeId === "page-text" || typeId === "page-quote") return !sanitizeInlineHtml(str(data.content)).trim();
  if (typeId === "page-code") return !str(data.content).trim();
  if (/^page-heading-[1-4]$/.test(typeId)) return !str(data.text).trim() && !sanitizeInlineHtml(str(data.content)).trim();
  if (/^page-(image|video|audio|file)$/.test(typeId)) {
    if (typeId === "page-image" && /^https?:\/\//i.test(str(data.imageUrl))) return false;
    if (typeId === "page-video" && str(data.videoUrl).trim()) return !linkedVideo(str(data.videoUrl).trim());
    const id = attachmentIdOf(data);
    return !(id && props.resolveFile?.(id)?.url);
  }
  if (typeId === "page-callout" || typeId === "page-card") return !(Array.isArray(block.elements) && block.elements.length);
  return false;
}

/** The page body, read-only. Renders nothing for an empty or malformed body. */
export function PageReadOnly(props: PageReadOnlyProps) {
  const root = (props.content ?? {}) as { elements?: unknown };
  const elements = Array.isArray(root.elements) ? root.elements : [];
  if (!elements.length) return null;
  return (
    <div className={props.className}>
      <Blocks elements={elements} props={props} />
    </div>
  );
}

export default PageReadOnly;
